/**
 * THROWING A ROW AWAY, and the two presses it takes.
 *
 * A test lead, a test call and a junk enquiry are the three rows this system had no way to remove,
 * and they are all the same act, so they get the same control in the same words wherever they are
 * drawn: enquiries and the calls on a household's page today.
 *
 * IT IS NOT CORAL AND IT IS NOT LOUD. Coral means exactly one thing in this interface, a gap
 * somebody has to close, and a discard is nobody's gap: it is the person deciding a row was never a
 * person. So it is a quiet control at the bottom of a panel, under everything worth doing first,
 * and it opens into a step rather than acting on the first press. A destructive control that fires
 * on one tap is how a real enquiry disappears from a phone in a car.
 *
 * THE REASON IS ASKED FOR AND NOT GUESSED. "It was a test" and "it was spam" are different facts
 * about the business, and the reason is the only place either is ever recorded: the server writes
 * it into the row's own notes in the same batch as the delete, and keeps no column for it.
 */
import { useState } from 'react';
import type { DiscardReason } from '@fk/shared';
import { ApiError, type Discard } from '../api.ts';
import {
  FORGET_WARNING,
  actionWords,
  doneWords,
  erasesThePerson,
  phoneSystemIn,
  reasonsOffered,
} from '../discard.ts';
import { orNull } from '../format.ts';
import { DISCARD_REASON_LABELS } from '../labels.ts';
import { Button, TextAreaField } from './Field.tsx';
import { ErrorNotice } from './Notice.tsx';

export function DiscardControl({
  idPrefix,
  /** What is being thrown away, in the words on the confirming button: "this enquiry", "this call". */
  what,
  /** What the screen says afterwards, in the sentence pattern the screen already uses. */
  doneSentence,
  /** One line under the opening control, saying what this does to the record. */
  hint,
  /**
   * Whether this door can honour a privacy request, which one of them can: the enquiry. The call
   * log, the expense book and the task list erase nothing, the server refuses the word at all
   * three, and a sheet offering it there would be a control that answers 422 to somebody who
   * believes she has just honoured a request to be forgotten. `discard.ts` has the whole of it.
   */
  canForget = false,
  onDiscard,
  onDone,
}: {
  idPrefix: string;
  what: string;
  doneSentence: string;
  hint: string;
  canForget?: boolean;
  onDiscard: (body: Discard) => Promise<unknown>;
  onDone: (sentence: string) => void;
}) {
  const [asked, setAsked] = useState(false);
  const [reason, setReason] = useState<DiscardReason>('test');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const field = (name: string) => `${idPrefix}-discard-${name}`;
  const erasing = erasesThePerson(reason);

  async function confirm() {
    setBusy(true);
    setProblem(null);
    try {
      // Typed words are dropped by the server on a privacy request, so they are neither asked for
      // above nor sent here: a note beside an erasure is how the erased person survives it.
      const answer = await onDiscard({ reason, notes: erasing ? null : orNull(notes) });
      // A forget's answer says whether the phone system was asked to erase her texts (ops #27).
      onDone(doneWords(reason, doneSentence, phoneSystemIn(answer)));
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
    } finally {
      setBusy(false);
    }
  }

  if (!asked) {
    return (
      <div>
        <Button tone="quiet" onClick={() => setAsked(true)}>
          Discard
        </Button>
        <p className="mt-1 text-xs text-navy/65">{hint}</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-navy/20 p-3">
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}

      <fieldset>
        <legend className="text-sm font-medium">Why it is going</legend>
        <div className="mt-2 flex flex-col gap-2">
          {reasonsOffered(canForget).map((option) => (
            <label
              key={option}
              htmlFor={field(option)}
              className={`flex min-h-11 desk:min-h-9 cursor-pointer items-center gap-3 rounded-lg border p-3 ${
                reason === option ? 'border-navy bg-gold/10' : 'border-navy/25'
              }`}
            >
              <input
                id={field(option)}
                type="radio"
                name={field('reason')}
                checked={reason === option}
                onChange={() => setReason(option)}
                className="h-5 w-5 shrink-0 accent-navy"
              />
              <span className="text-sm">{DISCARD_REASON_LABELS[option]}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {/*
       * A privacy request takes no typed words and gets a sentence instead, because every clause
       * of what happens next is a thing that cannot be taken back. It is not coral: coral is a gap
       * somebody has to close, and this is somebody being honoured rather than a gap.
       */}
      {erasing ? (
        <p className="mt-3 mb-4 rounded-lg border border-navy/20 bg-navy/5 p-3 text-sm">
          {FORGET_WARNING}
        </p>
      ) : (
        <div className="mt-3">
          <TextAreaField
            id={field('notes')}
            label="Anything to add"
            value={notes}
            onChange={setNotes}
            rows={2}
            hint="Optional. It is written onto the record with the reason."
          />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={confirm} disabled={busy}>
          {busy ? 'Recording' : actionWords(reason, what)}
        </Button>
        <Button
          tone="quiet"
          onClick={() => {
            setAsked(false);
            setProblem(null);
          }}
          disabled={busy}
        >
          Never mind
        </Button>
      </div>
    </div>
  );
}
